import React, { useState, useEffect, useCallback } from 'react';
import { 
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  DocumentTextIcon,
  FunnelIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import knowledgeService from '../../services/knowledgeService';

interface KnowledgeChunk {
  id: number;
  chunk_text: string;
  chunk_index: number;
  token_count: number;
  needs_review: boolean;
  extraction_method: string;
  created_at: string;
  document_id: number;
  document_name: string;
  category: string;
  has_image_content: boolean;
}

interface KnowledgeDocument {
  id: number;
  document_name: string;
  category: string;
}

const KnowledgeChunkBrowser: React.FC = () => {
  const [chunks, setChunks] = useState<KnowledgeChunk[]>([]);
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedDocument, setSelectedDocument] = useState('');
  const [selectedChunk, setSelectedChunk] = useState<KnowledgeChunk | null>(null);
  const [fullChunk, setFullChunk] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newChunkText, setNewChunkText] = useState('');
  const [processingAction, setProcessingAction] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const fetchChunks = useCallback(async (loadMore = false) => {
    try {
      setLoading(!loadMore);
      const params: any = {
        limit: 50,
        offset: loadMore ? chunks.length : 0
      };
      
      if (search.trim()) {
        params.search = search.trim();
      }
      
      if (selectedDocument) {
        params.document_id = selectedDocument;
      }

      const response = await knowledgeService.getKnowledgeChunks(params);
      
      if (loadMore) {
        setChunks(prev => [...prev, ...response.chunks]);
      } else {
        setChunks(response.chunks);
      }
      
      setTotal(response.total);
      setHasMore(response.hasMore);
    } catch (error) {
      console.error('Failed to fetch chunks:', error);
      toast.error('Failed to load knowledge chunks');
    } finally {
      setLoading(false);
    }
  }, [search, selectedDocument, chunks.length]);

  useEffect(() => {
    fetchChunks();
    fetchDocuments();
  }, [search, selectedDocument, fetchChunks]);

  const fetchDocuments = async () => {
    try {
      const response = await knowledgeService.getKnowledgeItems();
      setDocuments(response);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  };

  const handleSelectChunk = async (chunk: KnowledgeChunk) => {
    try {
      setSelectedChunk(chunk);
      const fullChunkData = await knowledgeService.getKnowledgeChunk(chunk.id);
      setFullChunk(fullChunkData);
      setEditedText(fullChunkData.chunk_text);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to fetch full chunk:', error);
      toast.error('Failed to load chunk details');
    }
  };

  const handleUpdateChunk = async () => {
    if (!selectedChunk) return;

    try {
      setProcessingAction('updating');
      await knowledgeService.updateKnowledgeChunk(selectedChunk.id, {
        chunk_text: editedText
      });

      toast.success('Chunk updated successfully!');
      
      // Refresh chunks and update selected chunk
      await fetchChunks();
      
      // Update the full chunk data
      const updatedFullChunk = await knowledgeService.getKnowledgeChunk(selectedChunk.id);
      setFullChunk(updatedFullChunk);
      
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update chunk:', error);
      toast.error('Failed to update chunk');
    } finally {
      setProcessingAction(null);
    }
  };

  const handleDeleteChunk = async (chunkId: number) => {
    if (!window.confirm('Are you sure you want to delete this chunk? This action cannot be undone.')) {
      return;
    }

    try {
      setProcessingAction('deleting');
      await knowledgeService.deleteKnowledgeChunk(chunkId);

      toast.success('Chunk deleted successfully!');
      
      // Refresh chunks
      await fetchChunks();
      
      // Clear selection if the deleted chunk was selected
      if (selectedChunk?.id === chunkId) {
        setSelectedChunk(null);
        setFullChunk(null);
      }
    } catch (error) {
      console.error('Failed to delete chunk:', error);
      toast.error('Failed to delete chunk');
    } finally {
      setProcessingAction(null);
    }
  };

  const handleCreateChunk = async () => {
    if (!newChunkText.trim()) {
      toast.error('Please enter some text for the new chunk');
      return;
    }

    try {
      setProcessingAction('creating');
      await knowledgeService.createKnowledgeChunk({
        chunk_text: newChunkText.trim(),
        knowledge_base_id: selectedDocument ? parseInt(selectedDocument) : undefined,
        category: 'manual'
      });

      toast.success('Knowledge chunk created successfully!');
      
      // Refresh chunks
      await fetchChunks();
      
      // Reset form
      setNewChunkText('');
      setShowAddModal(false);
    } catch (error) {
      console.error('Failed to create chunk:', error);
      toast.error('Failed to create chunk');
    } finally {
      setProcessingAction(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'vision': return 'bg-blue-100 text-blue-800';
      case 'manual': return 'bg-green-100 text-green-800';
      case 'direct': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <DocumentTextIcon className="h-8 w-8 text-indigo-600 mr-3" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Knowledge Chunk Browser</h2>
              <p className="text-gray-600 mt-1">
                View and manage individual chunks of knowledge in your database
              </p>
            </div>
          </div>
          
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Chunk
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search in chunk content..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Document Filter */}
          <div className="relative">
            <FunnelIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <select
              value={selectedDocument}
              onChange={(e) => setSelectedDocument(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Documents</option>
              {documents.map(doc => (
                <option key={doc.id} value={doc.id}>
                  {doc.document_name} ({doc.category})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 text-sm text-gray-600">
          Showing {chunks.length} of {total} chunks
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chunk List */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Knowledge Chunks</h3>
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-6">
                <div className="animate-pulse space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-20 bg-gray-200 rounded"></div>
                  ))}
                </div>
              </div>
            ) : chunks.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <DocumentTextIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No chunks found</p>
                {search && <p className="text-sm">Try adjusting your search or filters</p>}
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {chunks.map((chunk) => (
                  <div
                    key={chunk.id}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedChunk?.id === chunk.id ? 'bg-indigo-50 border-r-4 border-indigo-500' : ''
                    }`}
                    onClick={() => handleSelectChunk(chunk)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 mb-1">
                          {chunk.document_name}
                        </p>
                        <p className="text-xs text-gray-600 line-clamp-3">
                          {chunk.chunk_text}
                        </p>
                        <div className="flex items-center mt-2 space-x-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getMethodColor(chunk.extraction_method)}`}>
                            {chunk.extraction_method}
                          </span>
                          <span className="text-xs text-gray-500">
                            {chunk.token_count} tokens
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDate(chunk.created_at)}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end space-y-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteChunk(chunk.id);
                          }}
                          disabled={processingAction === 'deleting'}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                          title="Delete chunk"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {hasMore && (
                  <div className="p-4">
                    <button
                      onClick={() => fetchChunks(true)}
                      className="w-full py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded"
                    >
                      Load More
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Chunk Details/Editor */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Chunk Details</h3>
              {selectedChunk && (
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className="inline-flex items-center px-3 py-1 text-sm text-indigo-600 hover:bg-indigo-50 rounded-md"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  {isEditing ? 'Cancel' : 'Edit'}
                </button>
              )}
            </div>
          </div>
          
          {selectedChunk && fullChunk ? (
            <div className="p-6 space-y-4">
              {/* Chunk Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Chunk Information</h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <p><span className="font-medium">Document:</span> {fullChunk.document_name}</p>
                  <p><span className="font-medium">Category:</span> {fullChunk.category}</p>
                  <p><span className="font-medium">Index:</span> {fullChunk.chunk_index}</p>
                  <p><span className="font-medium">Tokens:</span> {fullChunk.token_count}</p>
                  <p><span className="font-medium">Method:</span> {fullChunk.extraction_method}</p>
                  <p><span className="font-medium">Created:</span> {formatDate(fullChunk.created_at)}</p>
                </div>
              </div>
              
              {/* Chunk Content */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Chunk Content</h4>
                {isEditing ? (
                  <div className="space-y-3">
                    <textarea
                      value={editedText}
                      onChange={(e) => setEditedText(e.target.value)}
                      className="w-full h-64 p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      placeholder="Edit the chunk content..."
                    />
                    
                    {/* Action Buttons */}
                    <div className="flex space-x-3">
                      <button
                        onClick={handleUpdateChunk}
                        disabled={processingAction === 'updating'}
                        className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        <PencilIcon className="h-4 w-4 mr-2" />
                        {processingAction === 'updating' ? 'Updating...' : 'Update'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-sm text-gray-700">
                      {fullChunk.chunk_text}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">
              <EyeIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Select a chunk to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Chunk Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Add New Knowledge Chunk</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chunk Content
                </label>
                <textarea
                  value={newChunkText}
                  onChange={(e) => setNewChunkText(e.target.value)}
                  className="w-full h-48 p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  placeholder="Enter the knowledge content..."
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setNewChunkText('');
                  }}
                  className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateChunk}
                  disabled={processingAction === 'creating'}
                  className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                  {processingAction === 'creating' ? 'Creating...' : 'Create Chunk'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeChunkBrowser;